import React, { useState, useEffect } from "react";
import { Loader2, Eye, ChevronLeft, ChevronRight, X } from "lucide-react";
import CustomModal from "../../common/CustomModal";
import { useAppDispatch, useAppSelector } from "../../../store/hook";
import { RootState } from "../../../store/store";
import { fetchFYPreview, clearFYPreview, closeFY } from "../../../features/distributor/DistributorSlice";
import type { DistributorProgram } from "../../../features/distributor/DistributorSlice";
import { formatDateForDisplay, nextCalendarDay } from "./yearEndUtils";
import CloseFYStepper, { type CloseFYStep } from "./CloseFYStepper";
import YearEndFormSection from "./YearEndFormSection";
import YearEndPreviewResult from "./YearEndPreviewResult";

export interface YearEndDiscountRange {
  min_order_value: number;
  max_order_value: number | null;
  discount: number | null;
}

interface YearEndModalProps {
  show: boolean;
  onHide: () => void;
  program: DistributorProgram | null;
  onCloseFySuccess?: () => void;
}

const YearEndModal: React.FC<YearEndModalProps> = ({ show, onHide, program, onCloseFySuccess }) => {
  const dispatch = useAppDispatch();
  const {
    fyPreviewData,
    fyPreviewLoading,
    fyPreviewError,
    fyCloseLoading,
    fyCloseError,
    fyCloseSuccessMessage,
  } = useAppSelector((state: RootState) => state.distributor);

  const [step, setStep] = useState<CloseFYStep>(1);
  const [currentFYEndDate, setCurrentFYEndDate] = useState("");
  const [newFYEndDate, setNewFYEndDate] = useState("");
  const [remark, setRemark] = useState("");
  const [discountRanges, setDiscountRanges] = useState<YearEndDiscountRange[]>([
    { min_order_value: 0, max_order_value: null, discount: null },
  ]);

  const currentFYStartDefault = program?.ProgramDistributorWallet?.contractStartDate ?? "";
  const currentFYStartDisplay = formatDateForDisplay(currentFYStartDefault);
  const newFYStartDate = currentFYEndDate ? nextCalendarDay(currentFYEndDate) : "";
  const newFYStartDisplay = newFYStartDate ? formatDateForDisplay(newFYStartDate) : "—";

  useEffect(() => {
    if (show && program) {
      setStep(1);
      dispatch(clearFYPreview());
      setCurrentFYEndDate("");
      setNewFYEndDate("");
      setRemark("");
      const existing = program.ProgramDistributorWallet?.discountRanges;
      if (existing?.length) {
        setDiscountRanges(
          existing.map((r) => ({
            min_order_value: r.min_order_value,
            max_order_value: r.max_order_value,
            discount: r.discount,
          }))
        );
      } else {
        setDiscountRanges([
          { min_order_value: 0, max_order_value: null, discount: null },
        ]);
      }
    }
  }, [show, program, dispatch]);

  const handleDiscountChange = (
    index: number,
    field: keyof YearEndDiscountRange,
    value: string
  ) => {
    const num = value === "" ? null : Number(value);
    setDiscountRanges((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: num };
      return next;
    });
  };

  const addRange = () => {
    const last = discountRanges[discountRanges.length - 1];
    const newMin = last ? (last.max_order_value ?? last.min_order_value) + 1 : 0;
    setDiscountRanges((prev) => [
      ...prev,
      { min_order_value: newMin, max_order_value: null, discount: null },
    ]);
  };

  const removeRange = (index: number) => {
    if (discountRanges.length <= 1) return;
    setDiscountRanges((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePreviewChanges = () => {
    if (!program) return;
    const walletId =
      program.ProgramDistributorWallet?.wallet_id;
    const newDiscountRanges = discountRanges.map((r) => ({
      min_order_value: r.min_order_value,
      max_order_value: r.max_order_value,
      discount: r.discount ?? 0,
    }));
    void dispatch(
      fetchFYPreview({
        walletId,
        currentFyEndDate: currentFYEndDate,
        newFyEndDate: newFYEndDate,
        newDiscountRanges,
        remark: remark.trim() || undefined,
      })
    )
      .then((result) => {
        if (fetchFYPreview.fulfilled.match(result)) setStep(3);
      })
      .catch(() => {
        /* stay on step 2; reducer sets fyPreviewError */
      });
  };

  const handleCloseFY = () => {
    if (!program) return;
    const walletId =
      program.ProgramDistributorWallet?.wallet_id;
    const newDiscountRanges = discountRanges.map((r) => ({
      min_order_value: r.min_order_value,
      max_order_value: r.max_order_value,
      discount: r.discount ?? 0,
    }));
    void dispatch(
      closeFY({
        walletId,
        currentFyEndDate: currentFYEndDate,
        newFyEndDate: newFYEndDate,
        newDiscountRanges,
        remark: remark.trim() || undefined,
      })
    ).then((result) => {
      if (closeFY.fulfilled.match(result)) {
        onHide();
        onCloseFySuccess?.();
      }
    });
  };

  const footer = (
    <div className="flex items-center justify-between w-full gap-2">
      <div className="flex gap-2">
        {step === 2 && (
          <button
            type="button"
            onClick={() => setStep(1)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none flex items-center gap-1.5"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
        )}
        {step === 3 && (
          <button
            type="button"
            onClick={() => setStep(2)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none flex items-center gap-1.5"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to edit
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 ml-auto">
        {step === 1 && (
          <button
            type="button"
            onClick={() => setStep(2)}
            disabled={!currentFYEndDate || !newFYEndDate}
            className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
        {step === 2 && (
          <button
            type="button"
            onClick={handlePreviewChanges}
            disabled={fyPreviewLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {fyPreviewLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                Preview Changes
              </>
            )}
          </button>
        )}
        {step === 3 && (
          <button
            type="button"
            onClick={handleCloseFY}
            disabled={fyCloseLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {fyCloseLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Closing...
              </>
            ) : (
              <>Close FY</>
            )}
          </button>
        )}
        <button
          type="button"
          onClick={onHide}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none"
        >
          Close
        </button>
      </div>
    </div>
  );

  return (
    <CustomModal
      show={show}
      onHide={onHide}
      title="Close FY"
      width="w-[980px] max-w-[98vw]"
      footer={footer}
    >
      <div className="w-full max-h-[70vh] overflow-y-auto overflow-x-auto pr-1 flex flex-col">
        {(fyPreviewError || fyCloseError) && (
          <div className="sticky top-0 z-10 shrink-0 p-3 pr-10 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 mb-4 shadow-sm relative">
            <span>{fyPreviewError || fyCloseError}</span>
            <button
              type="button"
              onClick={() => dispatch(clearFYPreview())}
              className="absolute top-2 right-2 p-1 rounded hover:bg-red-100 text-red-600 transition-colors"
              aria-label="Dismiss error"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {fyCloseSuccessMessage && (
          <div className="sticky top-0 z-0 shrink-0 p-3 pr-4 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 mb-4 shadow-sm">
            {fyCloseSuccessMessage}
          </div>
        )}
        <div className="pb-2 min-w-0">
          {program && (
            <>
              <CloseFYStepper currentStep={step} />

              {step === 1 && (
                <YearEndFormSection
                  step={1}
                  program={program}
                  currentFYStartDisplay={currentFYStartDisplay}
                  currentFYEndDate={currentFYEndDate}
                  onCurrentFYEndDateChange={setCurrentFYEndDate}
                  newFYStartDisplay={newFYStartDisplay}
                  newFYEndDate={newFYEndDate}
                  onNewFYEndDateChange={setNewFYEndDate}
                  remark={remark}
                  onRemarkChange={setRemark}
                  discountRanges={discountRanges}
                  onDiscountChange={handleDiscountChange}
                  onAddRange={addRange}
                  onRemoveRange={removeRange}
                />
              )}

              {step === 2 && (
                <YearEndFormSection
                  step={2}
                  program={program}
                  currentFYStartDisplay={currentFYStartDisplay}
                  currentFYEndDate={currentFYEndDate}
                  onCurrentFYEndDateChange={setCurrentFYEndDate}
                  newFYStartDisplay={newFYStartDisplay}
                  newFYEndDate={newFYEndDate}
                  onNewFYEndDateChange={setNewFYEndDate}
                  remark={remark}
                  onRemarkChange={setRemark}
                  discountRanges={discountRanges}
                  onDiscountChange={handleDiscountChange}
                  onAddRange={addRange}
                  onRemoveRange={removeRange}
                />
              )}

              {step === 3 && fyPreviewData && (
                <YearEndPreviewResult data={fyPreviewData} />
              )}
            </>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default YearEndModal;
